import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectClustersType } from 'shared/map-search/selectors/clustersType';
import { selectActiveFeature } from 'shared/map-search/selectors/features';
import { EClustersType } from 'shared/map-search/types/clustersType';
import { IHotspotProperties, TFeature } from 'shared/map-search/types/map';

import { useFeaturesContext } from '../../context';
import { checkIsHoverablePoint, getBalloonManagerByFeature } from '../utils';

interface IUsePointHandlersProps {
  onFeatureMouseEnter: (feature: TFeature, isBalloonVisible: boolean) => void;
  onFeatureMouseLeave: () => void;
  hoveredFeatureRef: React.RefObject<TFeature | null>;
  isRedesign: boolean;
  isResultsAvailable: boolean;
}

export const usePointHandlers = ({
  onFeatureMouseEnter,
  onFeatureMouseLeave,
  hoveredFeatureRef,
  isRedesign,
  isResultsAvailable,
}: IUsePointHandlersProps) => {
  const { hotspotsLayer, balloonManager, balloonPromoManager } = useFeaturesContext();

  const clustersType = useSelector(selectClustersType);
  const selectedFeature = useSelector(selectActiveFeature);
  const selectedFeatureRef = useRef(selectedFeature);

  const handlePointMouseEnter = useCallback(
    (event: YMaps.IEvent) => {
      const { originalFeature } = event
        .get<YMaps.IHotspotLayerObject<IHotspotProperties>>('activeObject')
        .getProperties();

      const isHoverablePoint = isRedesign
        ? selectedFeatureRef.current?.id !== originalFeature.id
        : checkIsHoverablePoint({
            selectedFeature: selectedFeatureRef.current,
            currentFeature: originalFeature,
          });

      if (!isHoverablePoint) {
        return;
      }

      const isBalloonBlocked =
        (clustersType === EClustersType.Peas && !originalFeature.properties.favoriteIds?.length) ||
        (isRedesign && !isResultsAvailable);

      if (isBalloonBlocked) {
        onFeatureMouseEnter(originalFeature, false);

        return;
      }

      const balloonManagerObject = getBalloonManagerByFeature({
        feature: originalFeature,
        balloonManager,
        balloonPromoManager,
      });
      const isBalloonVisible =
        !isRedesign ||
        clustersType === EClustersType.OpenablePeas ||
        Boolean(balloonManagerObject.objectManager.objects.getById(originalFeature.id));

      onFeatureMouseEnter(originalFeature, isRedesign && isBalloonVisible);

      if (!isBalloonVisible) {
        return;
      }

      if (clustersType === EClustersType.OpenablePeas) {
        /**
         * @description
         * Для кликабельных горошков (которые отрывают выдачу) при наведении добавляется балун с ценой
         */
        balloonManagerObject.objectManager.add({
          ...originalFeature,
          properties: {
            ...originalFeature.properties,
            isHovered: true,
          },
        });
      } else {
        /**
         * @description
         * Для детальных горошков обновляем hover у балуна
         */
        balloonManagerObject.updateBalloonProperties(originalFeature.id, {
          ...originalFeature.properties,
          isHovered: true,
        });
      }
    },
    [clustersType, onFeatureMouseEnter, balloonManager, balloonPromoManager, isRedesign, isResultsAvailable],
  );

  const handlePointMouseLeave = useCallback(() => {
    const hoveredFeature = hoveredFeatureRef.current;

    const isHoverablePoint = Boolean(
      hoveredFeature &&
      (isRedesign
        ? selectedFeatureRef.current?.id !== hoveredFeature.id
        : checkIsHoverablePoint({
            selectedFeature: selectedFeatureRef.current,
            currentFeature: hoveredFeature,
          })),
    );

    if (!hoveredFeature || !isHoverablePoint) {
      return;
    }

    onFeatureMouseLeave();

    if (
      (clustersType === EClustersType.Peas && !hoveredFeature.properties.favoriteIds?.length) ||
      (isRedesign && !isResultsAvailable)
    ) {
      return;
    }

    const balloonManagerObject = getBalloonManagerByFeature({
      feature: hoveredFeature,
      balloonManager,
      balloonPromoManager,
    });

    if (clustersType === EClustersType.OpenablePeas) {
      balloonManagerObject.objectManager.remove([hoveredFeature.id]);
    } else {
      balloonManagerObject.updateBalloonProperties(hoveredFeature.id, {
        ...hoveredFeature.properties,
        isHovered: false,
      });
    }
  }, [
    hoveredFeatureRef,
    clustersType,
    onFeatureMouseLeave,
    balloonManager,
    balloonPromoManager,
    isRedesign,
    isResultsAvailable,
  ]);

  useEffect(() => {
    if (!clustersType && !isRedesign) {
      return () => {};
    }

    hotspotsLayer.layer.events.add('mouseenter', handlePointMouseEnter);
    hotspotsLayer.layer.events.add('mouseleave', handlePointMouseLeave);

    return () => {
      hotspotsLayer.layer.events.remove('mouseenter', handlePointMouseEnter);
      hotspotsLayer.layer.events.remove('mouseleave', handlePointMouseLeave);
    };
  }, [hotspotsLayer, handlePointMouseEnter, handlePointMouseLeave, clustersType, isRedesign]);

  useEffect(() => {
    selectedFeatureRef.current = selectedFeature;
  }, [selectedFeature]);
};
